// <stacked-area-chart> — the catalogue dashboard's revenue-by-quarter
// chart: smooth stacked areas, series bottom-to-top in payload order
// (non-digital under digital, unclassified on top when present). Light-DOM
// enhancement of server-rendered markup, same contract as the other chart
// elements: the component renders the JSON payload and a sized canvas
// wrapper; this element only draws.
//
//   <stacked-area-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative h-56"><canvas></canvas></div>
//   </stacked-area-chart>
//
// The series legend is server-rendered HTML in the card header, not
// Chart.js's — so the Legend plugin never ships.

import {
	Chart,
	Filler,
	LineController,
	LineElement,
	PointElement,
} from "chart.js";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	money,
	readPayload,
	withAlpha,
} from "./chart-theme";

type Payload = {
	currency: string;
	// Stack order, bottom first.
	series: { label: string; color: string }[];
	// title is the tooltip heading ("Q2 2024"); the axis shows the shorter
	// label ("Q2 '24").
	points: { label: string; title?: string; values: number[] }[];
};

// The line controller/elements and the Filler plugin register lazily on the
// first area chart, so pages without one never pay for them — same pattern
// as the bubble map's geo pieces.
let lineRegistered = false;
function ensureLineRegistered() {
	if (lineRegistered) return;
	lineRegistered = true;
	Chart.register(LineController, LineElement, PointElement, Filler);
}

class StackedAreaChart extends HTMLElement {
	#chart: Chart<"line"> | null = null;

	connectedCallback() {
		ensureChartTheme();
		ensureLineRegistered();
		const payload = readPayload<Payload>(this, "stacked-area-chart");

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "stacked-area-chart"), {
			type: "line",
			data: {
				labels: payload.points.map((point) => point.label),
				datasets: payload.series.map((series, index) => {
					const color = chartColor(series.color);
					return {
						label: series.label,
						data: payload.points.map((point) => point.values[index] ?? 0),
						// Solid line over a translucent fill, each area stacking on
						// the one beneath it (the bottom series fills to the axis).
						borderColor: color,
						backgroundColor: withAlpha(color, 0.35),
						fill: index === 0 ? "origin" : "-1",
						borderWidth: 1.5,
						tension: 0.35,
						pointRadius: 0,
						pointHoverRadius: 3,
						pointHitRadius: 8,
						pointBackgroundColor: color,
						pointBorderColor: color,
					};
				}),
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: { duration: 300 },
				interaction: { mode: "index", intersect: false },
				plugins: {
					tooltip: {
						// Point-level title, per-series rows, exact total footer.
						callbacks: {
							title: (items) => {
								const first = items[0];
								if (!first) return "";
								return payload.points[first.dataIndex]?.title ?? first.label;
							},
							label: (item) =>
								` ${item.dataset.label}: ${money(item.parsed.y ?? 0, payload.currency)}`,
							footer: (items) => {
								const total = items.reduce(
									(sum, item) => sum + (item.parsed.y ?? 0),
									0,
								);
								return `Total: ${money(total, payload.currency)}`;
							},
						},
					},
				},
				scales: {
					x: {
						grid: { display: false },
						border: { display: false },
						ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 13 },
					},
					y: {
						stacked: true,
						// Hard floor at zero, same reasoning as the bar chart: a stray
						// net-negative quarter (a reversal-heavy statement alone in its
						// period) shouldn't rescale the whole axis. The index-mode
						// tooltip still shows the real figures.
						min: 0,
						border: { display: false },
						grid: { color: "rgba(255, 255, 255, 0.06)" },
						ticks: {
							maxTicksLimit: 5,
							callback: (value) =>
								money(Number(value), payload.currency, "compact"),
						},
					},
				},
			},
		});
	}

	disconnectedCallback() {
		this.#chart?.destroy();
		this.#chart = null;
	}
}

if (!customElements.get("stacked-area-chart")) {
	customElements.define("stacked-area-chart", StackedAreaChart);
}
